import i18n, { ResourceLanguage } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { DateTime } from 'luxon';
import { EXTENSIONS } from 'probonio-shared-ui/registry';
import { initReactI18next } from 'react-i18next';
import '../extension'; // Ensure extensions are registered before loading translations. This is done here and not in the main file to avoid race conditions with file loading.
import { TranslationExtensionInterface } from '../extension/interface/translation.extension.interface';
import de from './de';
import en from './en';

export enum LocaleEnum {
  DE = 'de',
  EN = 'en',
}

function registerCustomFormatters(instance: typeof i18n): void {
  const formatter = instance.services.formatter;
  if (!formatter) {
    return;
  }

  formatter.addCached('time', lng => {
    const locale = lng || instance.resolvedLanguage;
    return value => {
      value = value instanceof DateTime ? value.toJSDate() : value;
      if (!(value instanceof Date)) {
        return value;
      }
      return value.toLocaleDateString(locale ?? undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    };
  });

  formatter.addCached('datetime', lng => {
    const locale = lng || instance.resolvedLanguage;
    return value => {
      value = value instanceof DateTime ? value.toJSDate() : value;
      if (!(value instanceof Date)) {
        return value;
      }
      return value.toLocaleDateString(locale ?? undefined, {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    };
  });

  formatter.addCached('monthAndYear', lng => {
    const locale = lng || instance.resolvedLanguage;
    return value => {
      value = value instanceof DateTime ? value.toJSDate() : value;
      if (!(value instanceof Date)) {
        return value;
      }
      return value.toLocaleDateString(locale ?? undefined, { year: 'numeric', month: 'long' });
    };
  });

  formatter.addCached('monthAndYearShort', lng => {
    const locale = lng || instance.resolvedLanguage;
    return value => {
      value = value instanceof DateTime ? value.toJSDate() : value;
      if (!(value instanceof Date)) {
        return value;
      }
      return value.toLocaleDateString(locale ?? undefined, { year: 'numeric', month: 'short' });
    };
  });

  // Legacy `else` for Date: short numeric date (day / month / year). Used as `{{…, date}}`.
  formatter.addCached('date', lng => {
    const locale = lng || instance.resolvedLanguage;
    return value => {
      value = value instanceof DateTime ? value.toJSDate() : value;
      if (!(value instanceof Date)) {
        return value;
      }
      return value.toLocaleDateString(locale ?? undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    };
  });

  formatter.addCached('euro', lng => {
    const locale = lng || instance.resolvedLanguage;
    return value => {
      if (typeof value !== 'number') {
        return value;
      }
      const centToEuro = (value / 100).toLocaleString(locale ?? undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      if (locale?.startsWith('en')) {
        return `€${centToEuro}`;
      }
      return `${centToEuro}€`;
    };
  });

  formatter.addCached('euro-float', lng => {
    const locale = lng || instance.resolvedLanguage;
    return value => {
      if (typeof value !== 'number') {
        return value;
      }
      const centToEuro = (value / 100).toLocaleString(locale ?? undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      if (locale?.startsWith('en')) {
        return `€${centToEuro.replace(/[.,]00$/, '')}`;
      }
      return `${centToEuro.replace(/[.,]00$/, '')}€`;
    };
  });

  formatter.addCached('percent', () => {
    return value => {
      if (typeof value !== 'number') {
        return value;
      }
      return `${value * 100}%`;
    };
  });
}

function loadExtensionTranslations() {
  const extensionResources = EXTENSIONS.getProviders(TranslationExtensionInterface).reduce<{
    de: ResourceLanguage;
    en: ResourceLanguage;
  }>(
    (mergedTranslations, provider) => {
      const { de: deObject, en: enObject } = provider.translations;

      mergedTranslations.de[provider.moduleName] = deObject;
      mergedTranslations.en[provider.moduleName] = enObject;

      return mergedTranslations;
    },
    { de: {}, en: {} },
  );

  return {
    de: { ...de, extensions: extensionResources.de },
    en: { ...en, extensions: extensionResources.en },
  };
}

const extensionTranslations = loadExtensionTranslations();

void i18n
  .use(LanguageDetector)
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    resources: {
      de: extensionTranslations.de,
      en: extensionTranslations.en,
    },
    fallbackLng: 'de',
    interpolation: {
      escapeValue: false,
    },
    react: {
      transKeepBasicHtmlNodesFor: ['br', 'b', 'strong', 'i', 'em', 'p', 'ul', 'ol', 'li'],
    },
    debug: import.meta.env.DEV,
  })
  .then(() => {
    registerCustomFormatters(i18n);
  });

i18n.on('languageChanged', lng => {
  document.documentElement.setAttribute('lang', lng);
});

function hotReload(lang: string): void {
  const file = `./${lang}.ts`;
  if (import.meta.hot) {
    import.meta.hot.accept(file, updatedModule => {
      if (!updatedModule) {
        return;
      }
      const resources = updatedModule.default;
      Object.keys(resources).forEach(namespace => {
        void i18n.addResourceBundle(lang, namespace, resources[namespace], true, true);
      });
    });
  }
}

hotReload('de');
hotReload('en');

export default i18n;
